<template>
  <section class="w-full overflow-hidden">
    <div class="container z-grid v-page-section-container-padding">
      <!--
        `w-full` bounds this slider: a grid item wrapping a flex track with no width of its own lets
        Chromium's intrinsic sizing feed back on itself and runs the slides out to 2^24 px. Only a
        percentage width stops that, a `min-width` does not.

        `watch-slides-progress` sets `.swiper-slide-visible`, which `carousel--redesign` in
        sliders.scss needs to lift a slide out of the dimmed state it gives every slide.

        Matrix/MatrixCarousel.vue is the other slider styled this way and needs the same pair.
      -->
      <Swiper
        :slides-per-view="1"
        :speed="500"
        :loop="canLoop"
        :loop-additional-slides="loopAdditionalSlides"
        :watch-slides-progress="true"
        :breakpoints="breakpoints"
        class="carousel--redesign relative mb-0 w-full min-w-0 col-start-2 col-end-11 sm:col-span-full 2xl:col-start-2 2xl:col-end-12"
        @swiper="onSlidesUpdate"
        @transition-end="onTransitionEnd"
        @lock="isLocked = true"
        @unlock="isLocked = false"
      >
        <SwiperSlide v-for="(item, i) in items" :key="`img${i}`" class="rounded-[20px] overflow-hidden w-full">
          <MoleculeCardCover
            :icon-left="item.isPreludiumContent ? 'app-preludium' : ''"
            :icon-right="get(item, 'category.icon.slug')"
            :duration="item.duration ? item.duration : undefined"
            :title="item.title"
            :subtitle="get(item, 'collection.title')"
            :image="portrait && item.imageOverride ? item.imageOverride : item.image"
            :url="item.url"
            size="small"
            :portrait="portrait"
          />
        </SwiperSlide>
        <!--
          Hidden while the slider is locked, which is Swiper's own word for every slide fitting on
          screen at the current breakpoint. Its Navigation module normally does this through
          `.swiper-button-lock`, and that module is deliberately not in use here, so the `lock` and
          `unlock` events take its place. Without it the arrows stay visible and do nothing.
        -->
        <button v-show="!isLocked" type="button" class="swiper-button-prev hidden md:flex" :aria-label="t_('previous')" @click="swiper?.slidePrev()">
          <AtomIconChevronLeft class="w-full h-full" />
        </button>
        <button v-show="!isLocked" type="button" class="swiper-button-next hidden md:flex" :aria-label="t_('next')" @click="swiper?.slideNext()">
          <AtomIconChevronRight class="w-full h-full" />
        </button>
      </Swiper>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Swiper as SwiperClass } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
// Only for the base .swiper-button-prev/-next positioning; the Navigation module itself isn't used.
import 'swiper/css/navigation'
import get from 'lodash/get'

const props = defineProps({
  items: { type: Array as PropType<any[]>, required: true },
  portrait: { type: Boolean, required: false, default: false },
  slidesVisible: { type: Number, required: false, default: 2 },
})

const { t_ } = setupTranslationPrefixer('DiscoveryCarousel')

// Swiper's breakpoints are keyed by min-width ("at this width and up"), unlike slick's max-width
// breakpoints used before the Swiper migration. The arrows are driven directly off the Swiper instance
// below rather than through its Navigation module, so they're shown/hidden with a Tailwind class instead
// of a per-breakpoint `navigation` param - a breakpoint's `navigation: true` replaces the root param
// wholesale, which drops the prev/next element binding and leaves the buttons inert.
const breakpoints = computed(() => ({
  640: { slidesPerView: props.portrait ? 2 : 1 },
  768: { slidesPerView: 2 },
  1024: { slidesPerView: props.portrait ? 3 : 2 },
  1280: { slidesPerView: 3 },
  1366: { slidesPerView: props.slidesVisible },
}))

// The widest any breakpoint goes. `loop` cannot follow a breakpoint, since changing it needs a re-init,
// so both figures below are decided against this one width and hold at every smaller one.
const maxSlidesPerView = computed(() => Math.max(1, ...Object.values(breakpoints.value).map((breakpoint) => breakpoint.slidesPerView)))

// Loop as soon as there is more to show than fits. Below that Swiper locks itself, which hides the
// arrows and stops dragging, and a carousel that holds every slide at once needs neither.
const canLoop = computed(() => props.items.length > maxSlidesPerView.value)

/*
 * Loop mode moves `slidesPerGroup + loopAdditionalSlides` slides from the end to the front, so asking
 * for too many leaves nothing to the right of the viewport and the peek on that side only appears once
 * a move puts slides back. Keeping one spare either side means `items - loopedSlides >= slidesPerView
 * + 1`, which is the -2 below with `slidesPerGroup` at its default of 1. The cap of 3 is what keeps a
 * drag past the edge from showing blank space once the set is large enough to afford it.
 */
const loopAdditionalSlides = computed(() => Math.max(0, Math.min(3, props.items.length - maxSlidesPerView.value - 2)))

// shallowRef, not ref: a deep ref proxies the whole Swiper instance and unwraps its internals, which
// both costs needless reactivity on a third-party object and breaks its own methods' `this`.
const swiper = shallowRef<SwiperClass | null>(null)

// Swiper's `watchOverflow` decides this per breakpoint and keeps it current on resize; the template
// hides the arrows on it. Seeded from the instance because `lock` has already fired by then when the
// slider starts out locked.
const isLocked = ref(false)

function onSlidesUpdate(swiperInstance: SwiperClass) {
  swiper.value = swiperInstance
  isLocked.value = swiperInstance.isLocked
}

// Swiper initialises during hydration, before the v-for slides are in the DOM, and two things that
// normally happen on init are then skipped for good:
// - loopFix() never moves the wrap-around slides in front of the first one, so the carousel sits at
//   translate 0 with nothing to its left. Dragging past either edge showed blank space until enough
//   real slides had scrolled by for Swiper to catch up. Only relevant while `canLoop` holds: without
//   loop mode there are no wrap-around slides and loopFix() returns immediately.
// - watchSlidesProgress' swiper-slide-visible classes (used below to fade/scale the peeking slides) are
//   never computed, so the peek effect only appeared once the user started dragging.
// Running both from onMounted (after the slides have rendered) establishes them up front instead.
onMounted(async () => {
  await nextTick()
  if (!swiper.value) return
  swiper.value.update()
  swiper.value.loopFix()
})

// Swiper only (re-)computes swiper-slide-visible while a drag/transition is actively in progress, then
// drops it once the transition settles - the peek effect would flicker away at rest and only reappear
// on the next drag. Re-applying it once each transition ends keeps it visible while at rest too.
// updateSlidesClasses() (not the full update()) since sizes/progress are already current at this point -
// calling the full update() here previously caused a real infinite loop via loop mode's slide reshuffling.
function onTransitionEnd(swiperInstance: SwiperClass) {
  swiperInstance.updateSlidesClasses()
}
</script>
